import { memo, useCallback, useEffect, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  LayoutType,
  DRAWER_WIDTH,
  EMSMemberAccountType,
  ContextActions
} from 'utils/constants';
import useConfig from 'hooks/useConfig';
import { useAppSelector } from 'hooks/useAppStore';
import MenuList from 'components/layout/MainLayout/MenuList/MenuList';
import LogoSection from '../Header/LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';
import { useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';
import { MenuItemsCA } from 'routes/menu-items';
import {
  selectAccountInfo,
  selectSelectedEmsMemberAccount
} from 'features/account/accountSelectors';
import Drawer from 'components/layout/MainLayout/Sidebar/DrawerStyled';
import { isEqualWith } from 'lodash';
import {
  checkAllForbidden,
  getMenuItemsCa
} from 'components/layout/MainLayout/Sidebar/sidebarUtils';
import { ContextAction, ResourceContextAction } from 'models/contextActions';
import { selectEaiContextActions } from 'features/eai/selectors';
import { selectDispatcherContextActions } from 'features/dispatcher/selectors/dispatcherSelectors';
import { selectBalanceGroupDetail } from 'features/eai/selectors/balanceGroupSelectors';
import useFetchAndSetSidebarCA from 'components/layout/MainLayout/Sidebar/useFetchAndSetSidebarCA';

const { CA_EAI_ACCESS_USER_PAGE } = ContextActions.EAI;
const { CA_DISP_ACCESS_OPERATION_CALENDAR_PAGE } = ContextActions.DISPATCHER;

type SidebarProps = {
  errorShowing: boolean;
  loggedWithoutPermissions: boolean;
  showSideBar: boolean;
  setShowSideBar: (value: boolean) => void;
  setErrorShowing: (value: boolean) => void;
};

// ==============================|| COMPONENT ||============================== //

const Sidebar = ({
  errorShowing,
  loggedWithoutPermissions,
  showSideBar,
  setShowSideBar,
  setErrorShowing
}: SidebarProps) => {
  const theme = useTheme();
  const { drawerOpen, drawerType, layout } = useConfig();
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  useFetchAndSetSidebarCA();

  const accountInfo = useAppSelector(selectAccountInfo);
  const balanceGroupDetail = useAppSelector(selectBalanceGroupDetail);
  const selectedEmsMemberAccount = useAppSelector(
    selectSelectedEmsMemberAccount
  );
  const eaiContextActions: ResourceContextAction[] = useAppSelector(
    selectEaiContextActions
  );
  const dispatcherContextActions: ResourceContextAction[] = useAppSelector(
    selectDispatcherContextActions
  );

  const [menuItemsCA, setMenuItemsCA] = useState<MenuItemsCA | null>(null);
  const [oldDispatcherCA, setOldDispatcherCA] = useState<ContextAction[]>([]);
  const [oldEaiCA, setOldEaiCA] = useState<ContextAction[]>([]);

  useEffect(() => {
    setShowSideBar(false);

    const eaiCA = eaiContextActions.find(
      (ca) => ca.identificator === accountInfo?.sub
    )?.contextActions;

    const dispatcherCA = dispatcherContextActions.find(
      (ca) => ca.identificator === accountInfo?.sub
    )?.contextActions;

    const dispatcherCAForCurrentBalancingGroup = dispatcherContextActions.find(
      (ca) =>
        ca.identificator ===
        selectedEmsMemberAccount?.balancingGroupId?.toString()
    )?.contextActions;

    const isMarketParticipant =
      selectedEmsMemberAccount?.accountType ===
      EMSMemberAccountType.MARKET_PARTICIPANT;
    const canAccessUserPage = eaiCA?.find(
      (ca) => ca && ca.code === CA_EAI_ACCESS_USER_PAGE
    );
    const canAccessOperationCalendarPage = dispatcherCA?.find(
      (ca) => ca && ca.code === CA_DISP_ACCESS_OPERATION_CALENDAR_PAGE
    );

    if (
      !eaiCA ||
      !dispatcherCA ||
      (!dispatcherCAForCurrentBalancingGroup && isMarketParticipant) ||
      !canAccessUserPage ||
      !canAccessOperationCalendarPage ||
      !selectedEmsMemberAccount
    ) {
      // as we don't have all data -> do nothing
      return;
    }

    const isEaiCaSameAsBefore = isEqualWith(oldEaiCA, eaiCA);
    const isDispatcherCaSameAsBefore = isEqualWith(
      oldDispatcherCA,
      dispatcherCA
    );

    if (isDispatcherCaSameAsBefore && isEaiCaSameAsBefore) {
      setShowSideBar(true);
      return;
    }

    if (!isEqualWith(oldDispatcherCA, dispatcherCA)) {
      setOldDispatcherCA(dispatcherCA);
    }

    if (!isEqualWith(oldEaiCA, eaiCA)) {
      setOldEaiCA(eaiCA);
    }

    const menuCa = getMenuItemsCa(
      dispatcherCA,
      dispatcherCAForCurrentBalancingGroup ?? [],
      eaiCA
    );

    setMenuItemsCA(menuCa);

    if (checkAllForbidden(menuCa)) {
      !loggedWithoutPermissions &&
        // TODO: check for all and clean code ask about CA if they will be always
        eaiCA.find((ca) => ca && ca.code === CA_EAI_ACCESS_USER_PAGE) &&
        dispatcherCA?.find(
          (ca) => ca && ca.code === CA_DISP_ACCESS_OPERATION_CALENDAR_PAGE
        ) &&
        setErrorShowing(true);
    } else {
      setShowSideBar(true);
    }
  }, [
    eaiContextActions,
    dispatcherContextActions,
    selectedEmsMemberAccount,
    oldDispatcherCA,
    oldEaiCA,
    accountInfo?.sub,
    balanceGroupDetail,
    loggedWithoutPermissions,
    setShowSideBar,
    setErrorShowing
  ]);

  useEffect(() => {
    if (!selectedEmsMemberAccount) {
      setOldDispatcherCA([]);
      setOldEaiCA([]);
    }
  }, [selectedEmsMemberAccount]);

  const getDrawerContent = useCallback(
    (menuCa: MenuItemsCA) => {
      return (
        <PerfectScrollbar
          component='div'
          style={{
            height: !matchUpMd ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
            paddingLeft: drawerOpen ? '16px' : 0,
            paddingRight: drawerOpen ? '16px' : 0,
            marginTop: drawerOpen ? 0 : '20px'
          }}
        >
          <MenuList {...menuCa} />
        </PerfectScrollbar>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchUpMd, drawerOpen, menuItemsCA, drawerType]
  );

  const renderSideBar =
    showSideBar &&
    selectedEmsMemberAccount &&
    menuItemsCA &&
    !errorShowing &&
    (layout === LayoutType.VERTICAL_LAYOUT || matchDownMd);

  return renderSideBar ? (
    <Box
      component='nav'
      sx={{ flexShrink: { md: 0 }, width: matchUpMd ? DRAWER_WIDTH : 'auto' }}
      aria-label='mailbox folders'
    >
      {matchDownMd || (drawerType === LayoutType.MINI_DRAWER && drawerOpen) ? (
        <Drawer>
          {matchDownMd && (
            <Box sx={{ display: 'flex', p: 2 }}>
              <LogoSection />
            </Box>
          )}
          {getDrawerContent(menuItemsCA)}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant='permanent' open={drawerOpen}>
          <Box sx={{ display: 'flex', p: 2 }}>
            <LogoSection />
          </Box>
          {getDrawerContent(menuItemsCA)}
        </MiniDrawerStyled>
      )}
    </Box>
  ) : (
    <></>
  );
};

export default memo(Sidebar);
